import { dayKeyForBR } from "../../../lib/time";
import type { BookFormat, BookStatus } from "../../../services/firebase/booksService";

export type StreakDecision = {
  today: string;
  nextStreak: number;
};

export type MascotStage = "ovo" | "bebe" | "jovem" | "sabia";

export type StreakRecoveryEligibilityInput = {
  lastStreak?: number;
  lastReadDate?: string;
  freeStreakRecoveryUsed?: boolean;
  streakTickets?: number;
  todayIso?: string;
};

/**
 * BUG-47: usava `format(new Date(), "yyyy-MM-dd")` — fuso do DISPOSITIVO, não
 * o do produto. Para um leitor em UTC+0 às 21h BR o "hoje" já era o dia
 * seguinte, e a streak aparecia quebrada (ou recuperável) um dia antes da
 * hora. Toda decisão de dia útil no produto passa por `dayKeyForBR`.
 */
function resolveToday(isoDate?: string): string {
  return isoDate ?? dayKeyForBR();
}

/**
 * `"yyyy-MM-dd"` ± n dias, em aritmética de CALENDÁRIO pura.
 *
 * Substitui `format(addDays(parseISO(key), n), "yyyy-MM-dd")` do `date-fns`
 * (17/09, rodada de performance): este arquivo entra no entry pelo
 * `booksService`, e o `format` genérico do `date-fns` custava ~22 KB
 * minificados em todo boot para fazer conta de dia. Sem fuso de propósito —
 * a chave é um dia do calendário, e `Date.UTC` com dia fora do intervalo
 * normaliza mês e ano sozinho (`Date.UTC(2026, 0, 32)` = 1º de fevereiro).
 */
function shiftDayKey(dayKey: string, deltaDays: number): string {
  const [y, m, d] = dayKey.split("-").map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    throw new RangeError(`Invalid day key: ${dayKey}`);
  }
  return new Date(Date.UTC(y, m - 1, d + deltaDays)).toISOString().slice(0, 10);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function calculateReadingProgress(
  progress: number,
  format: BookFormat,
  totalPages?: number,
): number {
  if (format === "kindle") return Math.min(100, Math.max(0, progress));
  if (!totalPages || totalPages <= 0) return Math.max(0, progress);
  return Math.min(totalPages, Math.max(0, progress));
}

export function determineBookStatus(
  progress: number,
  format: BookFormat,
  totalPages?: number,
): BookStatus {
  if (format === "kindle") return progress >= 100 ? "read" : "reading";
  if (totalPages && totalPages > 0 && progress >= totalPages) return "read";
  return "reading";
}

export function isBookFinished(
  progress: number,
  format: BookFormat,
  totalPages?: number,
): boolean {
  return determineBookStatus(progress, format, totalPages) === "read";
}

export function calculatePagesEarned(totalPages: number): number {
  return totalPages > 0 ? totalPages : 250;
}

export function calculateNextStreak(
  lastReadDate: string | undefined,
  currentStreak: number,
  todayIso?: string,
): StreakDecision {
  const today = resolveToday(todayIso);
  const yesterday = shiftDayKey(today, -1);

  if (lastReadDate === today) {
    return { today, nextStreak: currentStreak };
  }

  if (lastReadDate === yesterday) {
    return { today, nextStreak: currentStreak + 1 };
  }

  return { today, nextStreak: 1 };
}

/**
 * Dia (BR) em que a streak QUEBROU, dado o último dia lido.
 *
 * Uma streak com `lastReadDate = D` continua viva durante todo o dia D+1 —
 * ler em D+1 a incrementa. Ela só morre à meia-noite que abre **D+2**. Logo o
 * dia da quebra é sempre `lastReadDate + 2`, numa relação 1:1 com o ciclo de
 * morte — é por isso que serve de chave estável para o dismissal do prompt.
 */
export function streakBreakDayKey(lastReadDate: string): string {
  return shiftDayKey(lastReadDate, 2);
}

/**
 * O prompt automático de recuperação (decisão do fundador, 22/07 — janela 48h).
 *
 * Dispara quando as três coisas valem ao mesmo tempo:
 *  1. existe o que recuperar (`lastStreak > 0`);
 *  2. a streak está **realmente morta** e morreu há ≤48h — ou seja, o último
 *     dia lido é anteontem (morreu na virada de hoje) ou antianteontem
 *     (morreu ontem). A partir de 3 dias sem ler, o ciclo passou;
 *  3. há recurso: free pass mensal disponível OU ao menos 1 ticket.
 *
 * **Por que não `lastReadDate === ontem`** (o predicado antigo, e o literal do
 * brief): com o último dia lido em ONTEM a streak ainda está VIVA — ler hoje a
 * continua. Pior, `recoverStreak` faz `currentStreak = lastStreak`, e
 * `lastStreak` guarda o valor ANTERIOR ao último incremento. No doc real do
 * fundador em 20/07 (`currentStreak=2`, `lastStreak=1`) aceitar o prompt
 * rebaixaria a sequência de 2 para 1 **e** queimaria o free pass. Oferecer
 * recuperação de streak viva é sempre um mau negócio pro usuário.
 *
 * **Por que `currentStreak` saiu da entrada** (lição do BUG-47): o contador só
 * é zerado pelo cron das 05h BR, então entre 00:00 e 05:00 ele ainda mostra a
 * sequência morta — condicionar a `currentStreak === 0` era justamente o que
 * tornava este prompt inalcançável. A morte se deriva de `lastReadDate`, que
 * é a única fonte que o servidor também usa.
 */
export function shouldPromptStreakRecovery(
  input: StreakRecoveryEligibilityInput,
): boolean {
  if (!input.lastReadDate) return false;

  const hasRecoveryBudget = !input.freeStreakRecoveryUsed || (input.streakTickets ?? 0) > 0;
  const hasPreviousStreak = (input.lastStreak ?? 0) > 0;

  return (
    isWithinStreakRecoveryWindow(input.lastReadDate, input.todayIso) &&
    hasPreviousStreak &&
    hasRecoveryBudget
  );
}

/**
 * Só a janela de 48h do item 2 acima, isolada.
 *
 * Existe exportada porque a tela `/streak` precisa distinguir "passou da
 * janela" de "sem recurso" para dizer coisas diferentes — e re-derivar a regra
 * lá seria abrir uma segunda fonte da verdade sobre quando a recuperação vale
 * (a doença do BUG-81). Aqui e no prompt automático a janela é a MESMA função.
 */
export function isWithinStreakRecoveryWindow(
  lastReadDate: string | undefined,
  todayIso?: string,
): boolean {
  if (!lastReadDate) return false;
  const today = resolveToday(todayIso);
  const diedOneDayAgo = shiftDayKey(today, -2);
  const diedTwoDaysAgo = shiftDayKey(today, -3);
  return lastReadDate === diedOneDayAgo || lastReadDate === diedTwoDaysAgo;
}

export function canUseFreeStreakRecovery(freeStreakRecoveryUsed?: boolean): boolean {
  return !freeStreakRecoveryUsed;
}

export function getMascotStage(streak: number): MascotStage {
  if (streak <= 0) return "ovo";
  if (streak <= 3) return "bebe";
  if (streak <= 7) return "jovem";
  return "sabia";
}

/** Próximo estágio evolutivo, ou `null` se já estiver no máximo (sábia). */
export function getNextMascotStage(stage: MascotStage): MascotStage | null {
  if (stage === "ovo") return "bebe";
  if (stage === "bebe") return "jovem";
  if (stage === "jovem") return "sabia";
  return null;
}

export function getDaysUntilNextMascotStage(streak: number): number {
  const stage = getMascotStage(streak);
  if (stage === "ovo") return 1;
  if (stage === "bebe") return Math.max(0, 4 - streak);
  if (stage === "jovem") return Math.max(0, 8 - streak);
  return 0;
}

// ---------------------------------------------------------------------------
// Analytics — páginas lidas por dia (delta não negativo)
// ---------------------------------------------------------------------------

export function readingDateKey(date: Date = new Date()): string {
  // Data LOCAL, como o `format` do date-fns fazia — o contrato desta função
  // não mudou (quem precisa do dia do produto usa `dayKeyForBR`).
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function pageEquivalentForDailyStats(
  rawProgress: number,
  fmt: BookFormat,
  totalPages: number,
): number {
  if (fmt === "kindle") {
    if (!totalPages || totalPages <= 0) return 0;
    const p = Math.min(100, Math.max(0, rawProgress));
    return (p / 100) * totalPages;
  }
  return Math.max(0, rawProgress);
}

/**
 * Diferença em páginas entre dois progressos.
 * - Kindle (mesmo formato): Math.floor((Δ% / 100) × totalPages); sem totalPages → 0 (sem chute de 250).
 * - Físico: diferença em páginas; retrocesso não reduz o acumulado diário.
 */
export function pagesReadDeltaNonNegative(
  oldProgress: number,
  newProgress: number,
  oldFormat: BookFormat,
  newFormat: BookFormat,
  oldTotalPages: number,
  newTotalPages: number,
): number {
  if (oldFormat === "kindle" && newFormat === "kindle") {
    const tp = newTotalPages > 0 ? newTotalPages : oldTotalPages;
    if (!tp || tp <= 0) return 0;
    const oldP = Math.min(100, Math.max(0, oldProgress));
    const newP = Math.min(100, Math.max(0, newProgress));
    const percentDelta = Math.max(0, newP - oldP);
    return Math.floor((percentDelta / 100) * tp);
  }

  const oldEq = pageEquivalentForDailyStats(oldProgress, oldFormat, oldTotalPages);
  const newEq = pageEquivalentForDailyStats(newProgress, newFormat, newTotalPages);
  return Math.max(0, Math.round(newEq - oldEq));
}
